import React from "react";
import {useParams, useHistory, Redirect} from "react-router-dom";
import {Helmet} from "react-helmet";

import UnlockButton from "../Interface/UnlockButton";
import {useEthereum} from "../../EthereumContext";
import Box from "../Interface/Box";
import {ReactComponent as BackIcon} from "../../assets/back.svg";
import Separator from "../Interface/Separator/Separator";
import Button from "../Interface/Button/Button";
import ProposalTransactions from "./ProposalTransactions";
import Timer from "../Interface/Timer";
import {convertBalance, splitDescription} from "../../utils";
import {useDispatch, useSelector} from "react-redux";
import {fetchProposals, fetchBlockNumber, fetchProposalDetails} from "../../redux/governor";
import BigNumber from "bignumber.js";
import {toast} from "react-toastify";
import { useWeb3React } from "@web3-react/core";

const MIN_QUORUM_VOTES = new BigNumber(4_000_000).times(10 ** 18);

function ProposalVoting() {
    const {id} = useParams();
    const history = useHistory();
    const {account} = useWeb3React();
    const {governor, provider} = useEthereum();
    const dispatch = useDispatch();

    const proposalEvent = useSelector(store => store.governor.proposals[id]);
    const proposal = useSelector(store => store.governor.proposalData[id]);
    const receipt = useSelector(store => store.governor.proposalUserReceipts[id]);
    const block = useSelector(store => store.governor.blockNumber);
    const userVotes = useSelector(store => store.govToken.userVotes);

    const {blockNumber} = useSelector(store => store.governor);

    React.useEffect(() => dispatch(fetchBlockNumber({provider})), [provider, dispatch]);
    React.useEffect(
        () => dispatch(fetchProposals({governor, blockNumber})),
        [governor, blockNumber, dispatch]
    );

    const [remaining, setRemaining] = React.useState(null);
    const [pending, setPending] = React.useState(null);

    React.useEffect(() => {
        dispatch(fetchProposalDetails({governor, account, block}, id));
    }, [id, governor, account, block, dispatch]);

    React.useEffect(() => {
        dispatch(fetchBlockNumber({provider}));
    }, [provider, dispatch]);

    React.useEffect(() => {
        if (block && proposal) {
            setRemaining((proposal.endBlock.toNumber() - block) * process.env.REACT_APP_BLOCK_TIME);
        }
    }, [block, proposal]);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            if (remaining) {
                setRemaining(remaining - 1000);
            }
        }, 1000);
        return () => clearTimeout(timer);
    });

    const onVote = async (vote) => {
        setPending(vote);
        try {
            const tx = await governor.castVote(parseInt(id), vote, {gasLimit: 2000000});
            await tx.wait();
            toast.info("Voting is completed");
        } catch {
            toast.error("Voting failed");
        } finally {
            setPending(null);
        }
    };

    if (proposal && block && (proposal.id.toString() !== id || proposal.endBlock <= block)) {
        return <Redirect to="/voting" />;
    }

    if (!proposal || !proposalEvent || !block || !receipt) {
        return null;
    }

    const totalVotes = (new BigNumber(proposal.forVotes.toString())).plus(new BigNumber(proposal.againstVotes.toString()));
    const forPercent = (new BigNumber(proposal.forVotes.toString())).div(totalVotes).times(100);
    const againstPercent = (new BigNumber(proposal.againstVotes.toString())).div(totalVotes).times(100);
    const goingToSucceed = totalVotes.gt(0) && forPercent.gt(againstPercent);
    const isEnoughVotesCollected = totalVotes.gt(MIN_QUORUM_VOTES);

    const remainingVotes = () => {
        const diff = MIN_QUORUM_VOTES.minus(totalVotes).div(10 ** 18).toNumber();

        if(diff > 0) {
            return diff.toLocaleString("ru-RU", { maximumFractionDigits: 2 })
        } else {
            return "0";
        }
    };

    let forButton = null;
    if (account) {
        if (!receipt.hasVoted) {
            forButton = (
                <Button
                    onClick={() => onVote(true)}
                    style={{color: "white", backgroundColor: "var(--green1)", border: "none", marginTop: 20}}
                    disabled={pending != null}
                    blinking={pending != null && pending === true}
                >
                    Vote
                </Button>
            );
        } else if (receipt.support) {
            forButton = (
                <span style={{marginTop: 20, fontWeight: 600, color: "var(--green1)"}}>You voted For</span>
            );
        }
    } else {
        forButton = <UnlockButton />;
    }

    let againstButton = null;
    if (account) {
        if (!receipt.hasVoted) {
            againstButton = (
                <Button
                    onClick={() => onVote(false)}
                    style={{color: "white", backgroundColor: "var(--red)", border: "none", marginTop: 20}}
                    disabled={pending != null}
                    blinking={pending != null && pending === false}
                >
                    Vote
                </Button>
            );
        } else if (!receipt.support) {
            againstButton = (
                <span style={{marginTop: 20, fontWeight: 600, color: "var(--red)"}}>You voted Against</span>
            );
        }
    } else {
        againstButton = <UnlockButton />;
    }

    const {title, other} = splitDescription(proposalEvent.description);

    return (
        <div style={{width: "100%"}}>
            <Helmet>
                <title>{title} - Roobee DAO</title>
            </Helmet>
            <div style={{display: "flex", justifyContent: "space-between", width: "100%", height: 48}}>
                <div style={{display: "flex", alignItems: "center", cursor: "pointer"}} onClick={() => history.goBack()}>
                    <BackIcon />
                    <span style={{marginLeft: 10, fontSize: 14, color: "var(--grey1)", fontWeight: 600}}>Back</span>
                </div>
                {!account && <UnlockButton />}
            </div>
            <h2 style={{marginTop: 0, marginBottom: 40}}>{title}</h2>
            <Box direction="column" align="flex-start">
                <div style={{width: "100%", display: "flex", justifyContent: "space-between"}}>
                    <span>Voting</span>
                    <div style={{display: "flex"}}>
                        <span style={{color: "var(--redOrange)", marginRight: 10, fontWeight: 600}}>Ends In:</span>
                        <Timer remaining={remaining} />
                    </div>
                </div>
                <Separator />
                <div style={{width: "100%", display: "flex", justifyContent: "space-between"}}>
                    <div style={{width: "50%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center"}}>
                        <span style={{fontWeight: 600}}>For</span>
                        <span style={{color: "var(--grey0)", marginTop: 10}}>
                            {convertBalance(proposal.forVotes)}
                        </span>
                        {forButton}
                    </div>
                    <div style={{width: 1, height: 130, backgroundColor: "var(--grey3)", marginTop: 40, marginBottom: 20}} />
                    <div style={{width: "50%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center"}}>
                        <span style={{fontWeight: 600}}>Against</span>
                        <span style={{color: "var(--grey0)", marginTop: 10}}>
                            {convertBalance(proposal.againstVotes)}
                        </span>
                        {againstButton}
                    </div>
                </div>
                <span style={{textAlign: "center", width: "100%", color: goingToSucceed && isEnoughVotesCollected ? "var(--green1)" : "var(--red)", fontWeight: 800}}>
                    Proposal is going to {goingToSucceed && isEnoughVotesCollected ? "Succeed " : "Fail "}
                    {totalVotes.gt(0) && `(${forPercent.toFixed(0)}% For, ${againstPercent.toFixed(0)}% Against)`}
                    <br />
                    {!isEnoughVotesCollected
                      && `${remainingVotes()} more votes need to pass minimal threshold`
                    }
                </span>
            </Box>
            <Box direction="column" align="flex-start" style={{marginTop: 20}}>
                <span>Details</span>
                <Separator />
                {proposalEvent.signatures.length > 0 && (
                    <React.Fragment>
                        <ProposalTransactions proposal={proposalEvent} />
                        <Separator short />
                    </React.Fragment>
                )}
                <div style={{marginTop: 20, color: "var(--grey0)", whiteSpace: "pre-wrap"}}>
                    {other}
                </div>
            </Box>
        </div>
    );
}

export default ProposalVoting;
